import React, { useState, useEffect, useRef, useCallback } from "react";
// import Slider from 'react-slick'; // 不再需要 react-slick
// import 'slick-carousel/slick/slick.css'; // 不再需要
// import 'slick-carousel/slick/slick-theme.css'; // 不再需要
import "./ActiveKeyTimeline.less"; // 你的 CSS 文件，其中包含了进度条和控制按钮的样式
import {LightningPauseIcon, LightningPlayIcon} from "@/resource/IconComponents";
import { LightningSearchTypeEnum } from "@/util/cosntants";

// 假设你的 IMapImage 接口，这里只用到 title
interface IMapImage {
    key: string;
    imageUrl: string;
    title: string; // 用于进度条上的显示
}

interface ActiveKeyTimelineProps {
    keysToProcess: string[]; // 所有需要遍历的 activeKey 列表 (例如日期字符串)
    // imagesData: IMapImage[]; // 包含 key 和 title 的数据，用于显示标题和匹配进度
    currentTitle: string;
    initialKey?: string | null; // 初始活跃的 key
    onActiveKeyChange: (index: number) => void; // 通知父组件当前活跃的 key
    autoplaySpeed?: number; // 自动播放速度，默认为 2000ms
    currentKeyIndex?: number;
    searchType: LightningSearchTypeEnum;
}

// ProgressBar 组件 (几乎保持不变，但为了独立性，复制过来)
const ProgressBar = ({
    totalSegments, // 总段数，对应 keysToProcess.length
    activeIndex, // 当前活跃的索引
    duration = 2000,
    isPlaying,
    onSegmentClick // 新增：点击进度条上某个点时触发
}: {
    totalSegments: number;
    activeIndex: number;
    duration?: number;
    isPlaying: boolean;
    onSegmentClick: (index: number) => void;
}) => {
    const [progress, setProgress] = useState(0);
    const progressIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined); // 使用 Ref 存储 interval ID
    const segmentWidth = totalSegments > 0 ? 100 / totalSegments : 0;
    const updateInterval = 50; // 每50ms更新一次进度条动画
    const incrementPerTick = (totalSegments > 0) ? (segmentWidth / (duration / updateInterval)) : 0;

    useEffect(() => {
        // 清除上一个定时器，避免内存泄漏和多个定时器并行
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }

        // 当不播放或只有一个片段时，直接设置进度到当前片段的开始
        if (!isPlaying || totalSegments <= 1) {
            setProgress(activeIndex * segmentWidth);
            return;
        }

        // 重置进度到当前段的开始
        setProgress(activeIndex * segmentWidth);

        // 启动平滑动画
        const interval = setInterval(() => {
            setProgress(prev => {
                const nextProgress = prev + incrementPerTick;
                const targetEndProgress = (activeIndex + 1) * segmentWidth;

                // 如果已经达到或超过当前段的终点
                if (nextProgress >= targetEndProgress) {
                    return targetEndProgress; // 停止在当前段的末尾
                }
                return nextProgress;
            });
        }, updateInterval);

        progressIntervalRef.current = interval; // 保存定时器ID

        // 清理函数：组件卸载或依赖变化时清除定时器
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = undefined;
            }
        };
    }, [activeIndex, isPlaying, segmentWidth, duration, totalSegments, incrementPerTick, updateInterval]);

    const getBulletColor = useCallback((index: number) => {
        return index === activeIndex ? "#1890ff" : "#999";
    }, [activeIndex]);

    const handleProgressClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (totalSegments === 0) return;
        const progressBar = event.currentTarget;
        const rect = progressBar.getBoundingClientRect();
        const clickX = event.clientX - rect.left; // 点击位置相对于进度条左侧的距离
        const clickPercentage = (clickX / rect.width) * 100; // 点击位置的百分比

        const clickedSegmentIndex = Math.floor(clickPercentage / segmentWidth);
        if (clickedSegmentIndex >= 0 && clickedSegmentIndex < totalSegments) {
            onSegmentClick(clickedSegmentIndex);
        }
    }, [totalSegments, segmentWidth, onSegmentClick]);


    return (
        <div style={{
            position: "relative",
            // width: "100%",
            height: "15px",
            flexGrow: 1,
            minWidth: "200px",
            marginLeft: "5px",
            boxSizing: "border-box",
            overflow: "hidden",
        }}>
            <div
                style={{
                    height: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer" // 增加手型光标
                }}
                onClick={handleProgressClick} // 添加点击事件
            >
                <div
                    style={{
                        width: `${progress}%`,
                        height: "100%",
                        backgroundColor: "#1890ff",
                        borderRadius: "4px",
                        transition: `width ${updateInterval / 1000}s linear`
                    }}
                />

                {/* 幻灯片分隔线（刻度线） */}
                {Array.from({ length: totalSegments - 1 }).map((_, index) => (
                    <div
                        key={`separator-${index}`}
                        style={{
                            position: "absolute",
                            left: `${(index + 1) * segmentWidth}%`,
                            top: "0",
                            height: "100%",
                            width: "1px",
                            backgroundColor: "rgba(0,0,0,0.3)",
                            zIndex: 1
                        }}
                    />
                ))}

                {/* 刻度点（小圆点） */}
                {/* {Array.from({ length: totalSegments }).map((_, index) => (
                    <div
                        key={`node-${index}`}
                        style={{
                            position: "absolute",
                            left: `${index * segmentWidth + (segmentWidth / 2)}%`,
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "6px",
                            height: "6px",
                            backgroundColor: getBulletColor(index),
                            borderRadius: "50%",
                            zIndex: 2,
                            border: "1px solid #fff"
                        }}
                    />
                ))} */}
            </div>
        </div>
    );
};

// ----------------------------------------------------
// ActiveKeyTimeline 组件 - 替代原有的 ImageCarousel
// ----------------------------------------------------
const ActiveKeyTimeline: React.FC<ActiveKeyTimelineProps> = ({
    keysToProcess,
    // imagesData, // 传入图片数据，用于匹配标题
    currentTitle,
    initialKey,
    onActiveKeyChange,
    autoplaySpeed = 2000,
    currentKeyIndex,
    searchType
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0); // 内部维护的当前索引
    const autoplayIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Effect 1: 处理 keysToProcess 或 initialKey 变化，初始化组件状态
    useEffect(() => {
        if(searchType === LightningSearchTypeEnum.FIRSTSTRIKE &&
             currentKeyIndex === -1) return;

        if (keysToProcess.length === 0) {
            setIsPlaying(false);
            setActiveIndex(-1); // 没有数据时索引设为 -1
            onActiveKeyChange(-1); // 通知父组件没有活跃的 key
            return;
        }

        const initialIdx = initialKey ? keysToProcess.indexOf(initialKey) : 0;
        const validInitialIdx = initialIdx >= 0 ? initialIdx : 0;

        setActiveIndex(validInitialIdx);
        onActiveKeyChange(validInitialIdx); // 通知父组件初始活跃 key
        // 2. 这里不再根据 keysToProcess.length 来设置 isPlaying，
        // 保持由用户行为控制 isPlaying 的初始值 (false)
        // setIsPlaying(keysToProcess.length > 1);

        // 当 keysToProcess 变化时，如果只有一项，确保暂停
        if (keysToProcess.length <= 1) {
            setIsPlaying(false);
        }

    }, [keysToProcess, initialKey, onActiveKeyChange]); // 依赖项：keysToProcess, initialKey, onActiveKeyChange

    // Effect 2: 处理自动播放逻辑
    useEffect(() => {
        // 清除旧的定时器
        if (autoplayIntervalRef.current) {
            clearInterval(autoplayIntervalRef.current);
        }

        // 只有当 isPlaying 且有多个 key 时才启动自动播放
        if (isPlaying && keysToProcess.length > 1) {
            const interval = setInterval(() => {
                setActiveIndex(prevIndex => {
                    const nextIndex = (prevIndex + 1) % keysToProcess.length;
                    onActiveKeyChange(nextIndex); // 切换时通知父组件
                    return nextIndex;
                });
            }, autoplaySpeed);
            autoplayIntervalRef.current = interval;
        }

        // 清理函数：组件卸载或 isPlaying/keysToProcess 变化时清除定时器
        return () => {
            if (autoplayIntervalRef.current) {
                clearInterval(autoplayIntervalRef.current);
                autoplayIntervalRef.current = undefined;
            }
        };
    }, [isPlaying, keysToProcess, onActiveKeyChange, autoplaySpeed]);

    // 处理播放/暂停按钮点击
    const togglePlayPause = useCallback(() => {
        setIsPlaying(prevIsPlaying => {
            const newIsPlaying = !prevIsPlaying;
            // 3. 只有当点击播放且有多于一个数据点时才允许播放
            if (newIsPlaying && keysToProcess.length <= 1) {
                return false; // 如果只有一项，强制为暂停状态
            }
            return newIsPlaying;
        });
    }, [keysToProcess.length]); // 依赖 keysToProcess.length 来确保逻辑正确

    // 处理进度条点击，手动切换 activeKey
    const handleProgressBarClick = useCallback((index: number) => {
        if (index !== activeIndex) {
            setActiveIndex(index);
            setIsPlaying(false); // 手动点击后暂停自动播放
            onActiveKeyChange(index); // 通知父组件
        }
    }, [activeIndex, keysToProcess, onActiveKeyChange]);

    // const currentTitle = imagesData.find(img => img.key === keysToProcess[activeIndex])?.title || '';

    if (keysToProcess.length === 0) {
        return (
            <div className="active-key-timeline-wrapper">
                <div className="no-data-placeholder">没有可显示的数据。</div>
            </div>
        );
    }

    return (
        <div className="active-key-timeline-wrapper">
            <div className="carousel-controls no-screenshot">
                {/* 播放/暂停按钮 */}
                <button onClick={()=>{
                    if(searchType === LightningSearchTypeEnum.FIRSTSTRIKE && currentKeyIndex === -1){
                        onActiveKeyChange(0);
                    }
                    togglePlayPause();
                }} className="play-pause-button">
                    {isPlaying ? <LightningPauseIcon /> : <LightningPlayIcon />}
                </button>

                {/* 当前活跃 key 的标题 */}
                <span className="current-title">
                    {/* {currentTitle + " m" } */}
                    {currentTitle ? `${currentTitle} m` : "..."}
                </span>

                {/* 进度条组件 */}
                <ProgressBar
                    totalSegments={keysToProcess.length}
                    activeIndex={activeIndex}
                    duration={autoplaySpeed}
                    isPlaying={isPlaying}
                    onSegmentClick={handleProgressBarClick} // 传递点击回调
                />
            </div>
        </div>
    );
};

export default React.memo(ActiveKeyTimeline);